// Admin panel for user management

import { fetchPendingUsers, fetchAllUsers, approveUser, suspendUser, fetchBases, setUserProfileLimit } from './auth';

export interface AdminPanelOptions {
  container: HTMLElement;
  token: string;
  onClose: () => void;
}

export async function renderAdminPanel(options: AdminPanelOptions): Promise<void> {
  const { container, token, onClose } = options;

  const panel = document.createElement('div');
  panel.className = 'admin-panel';

  panel.innerHTML = `
    <div class="admin-header">
      <h2>Admin</h2>
      <button class="admin-close" aria-label="Close">&times;</button>
    </div>
    <div class="admin-content">
      <section class="admin-section">
        <h3>Pending Users</h3>
        <div id="admin-pending-list">
          <div class="loading-spinner">Loading...</div>
        </div>
      </section>
      <section class="admin-section">
        <h3>All Users</h3>
        <div id="admin-users-list">
          <div class="loading-spinner">Loading...</div>
        </div>
      </section>
      <section class="admin-section">
        <h3>Base Profiles</h3>
        <div id="admin-bases-list">
          <div class="loading-spinner">Loading...</div>
        </div>
      </section>
    </div>
  `;

  container.appendChild(panel);

  // Close button handler
  panel.querySelector('.admin-close')?.addEventListener('click', () => {
    panel.remove();
    onClose();
  });

  // Close on escape key
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      panel.remove();
      onClose();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  const pendingList = panel.querySelector('#admin-pending-list') as HTMLElement;
  const usersList = panel.querySelector('#admin-users-list') as HTMLElement;
  const basesList = panel.querySelector('#admin-bases-list') as HTMLElement;

  // Bases are read-only and don't change as a side effect of approve/deny/
  // suspend, so they're loaded once here rather than folded into
  // refreshLists (which those actions re-run).
  await Promise.all([
    refreshLists(pendingList, usersList, token),
    loadBases(basesList, token),
  ]);
}

async function refreshLists(pendingList: HTMLElement, usersList: HTMLElement, token: string): Promise<void> {
  await Promise.all([
    loadPendingUsers(pendingList, token, usersList),
    loadAllUsers(usersList, token, pendingList),
  ]);
}

async function loadPendingUsers(container: HTMLElement, token: string, usersListEl: HTMLElement): Promise<void> {
  container.innerHTML = '<div class="loading-spinner">Loading...</div>';

  try {
    const users = await fetchPendingUsers(token);

    if (users.length === 0) {
      container.innerHTML = '<p class="admin-empty">No pending users</p>';
      return;
    }

    container.innerHTML = users.map(u => `
      <div class="admin-user-card" data-id="${escapeHtml(u.id)}">
        <div class="admin-user-info">
          <span class="admin-user-name">${escapeHtml(u.display_name)}</span>
          <span class="admin-user-email">${escapeHtml(u.email)}</span>
          <span class="admin-user-meta">${escapeHtml(u.provider)} &middot; ${formatDate(u.created_at)}</span>
        </div>
        <div class="admin-user-actions">
          <button class="btn-approve" title="Approve">Approve</button>
          <button class="btn-deny" title="Deny">Deny</button>
        </div>
      </div>
    `).join('');

    container.querySelectorAll('.btn-approve').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const card = (e.target as HTMLElement).closest('.admin-user-card') as HTMLElement;
        const id = card.dataset.id;
        if (!id) return;
        try {
          await approveUser(token, id);
          await refreshLists(container, usersListEl, token);
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to approve user');
        }
      });
    });

    container.querySelectorAll('.btn-deny').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const card = (e.target as HTMLElement).closest('.admin-user-card') as HTMLElement;
        const id = card.dataset.id;
        if (!id) return;
        if (!confirm('Deny this user? They will be suspended.')) return;
        try {
          await suspendUser(token, id);
          await refreshLists(container, usersListEl, token);
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to deny user');
        }
      });
    });
  } catch (err) {
    container.innerHTML = `<p class="error-message">${err instanceof Error ? err.message : 'Failed to load pending users'}</p>`;
  }
}

async function loadBases(container: HTMLElement, token: string): Promise<void> {
  container.innerHTML = '<div class="loading-spinner">Loading...</div>';

  try {
    const bases = await fetchBases(token);

    if (bases.length === 0) {
      container.innerHTML = '<p class="admin-empty">No base profiles</p>';
      return;
    }

    // Read-only by design: a syntax error pushed to a base from here would
    // break every profile that inherits from it, so editing bases is out of
    // scope for this panel.
    container.innerHTML = bases.map(b => `
      <div class="admin-base-row" data-base="${escapeHtml(b.name)}">
        <span class="admin-base-name">${escapeHtml(b.name)}</span>
        <span class="admin-base-version">v${b.version}</span>
      </div>
    `).join('');
  } catch (err) {
    container.innerHTML = `<p class="error-message">${err instanceof Error ? err.message : 'Failed to load base profiles'}</p>`;
  }
}

async function loadAllUsers(container: HTMLElement, token: string, pendingListEl: HTMLElement): Promise<void> {
  container.innerHTML = '<div class="loading-spinner">Loading...</div>';

  try {
    const users = await fetchAllUsers(token);

    if (users.length === 0) {
      container.innerHTML = '<p class="admin-empty">No users</p>';
      return;
    }

    container.innerHTML = users.map(u => `
      <div class="admin-user-card" data-id="${escapeHtml(u.id)}" data-user="${escapeHtml(u.id)}">
        <div class="admin-user-info">
          <span class="admin-user-name">
            ${escapeHtml(u.display_name)}
            <span class="admin-status-badge admin-status-${escapeHtml(u.status)}">${escapeHtml(u.status)}</span>
            ${u.is_admin ? '<span class="admin-status-badge admin-status-admin">admin</span>' : ''}
          </span>
          <span class="admin-user-email">${escapeHtml(u.email)}</span>
          <span class="admin-user-meta">Ports ${u.port}&ndash;${u.port + 7} &middot; ${formatDate(u.created_at)}</span>
          <div class="admin-user-limits">
            <label>
              Profile cap
              <input
                type="number"
                class="admin-max-profiles"
                min="1"
                placeholder="default"
                title="How many profiles this user may have. No upper limit — a profile costs a directory, not a port."
                value="${u.max_profiles ?? ''}"
              >
            </label>
            <label>
              Running at once
              <input
                type="number"
                class="admin-max-concurrent"
                min="1"
                max="8"
                placeholder="default"
                title="How many of their profiles may run at the same time. Capped at 8: that is how many ports a container publishes."
                value="${u.max_concurrent ?? ''}"
              >
            </label>
            <button class="admin-save-limits">Save</button>
          </div>
        </div>
        ${!u.is_admin && u.status === 'approved' ? `
          <div class="admin-user-actions">
            <button class="btn-suspend" title="Suspend">Suspend</button>
          </div>
        ` : ''}
      </div>
    `).join('');

    container.querySelectorAll('.btn-suspend').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const card = (e.target as HTMLElement).closest('.admin-user-card') as HTMLElement;
        const id = card.dataset.id;
        if (!id) return;
        if (!confirm('Suspend this user? Their container will be stopped.')) return;
        try {
          await suspendUser(token, id);
          await refreshLists(pendingListEl, container, token);
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to suspend user');
        }
      });
    });

    container.querySelectorAll('.admin-save-limits').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const card = (e.target as HTMLElement).closest('.admin-user-card') as HTMLElement;
        const id = card?.dataset.user;
        if (!id) return;
        const input = card.querySelector('.admin-max-profiles') as HTMLInputElement;
        const raw = input.value.trim();
        const maxProfiles = raw === '' ? null : Number(raw);
        const concurrentInput = card.querySelector('.admin-max-concurrent') as HTMLInputElement;
        const concurrentRaw = concurrentInput.value.trim();
        const maxConcurrent = concurrentRaw === '' ? null : Number(concurrentRaw);
        try {
          await setUserProfileLimit(token, id, maxProfiles, maxConcurrent);
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Failed to update profile limits');
        }
      });
    });
  } catch (err) {
    container.innerHTML = `<p class="error-message">${err instanceof Error ? err.message : 'Failed to load users'}</p>`;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// CSS for admin panel
const adminStyles = `
.admin-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 500px;
  height: 100vh;
  background: #1e1e2e;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #e0e0e0;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #333;
}

.admin-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #00d4aa;
}

.admin-close {
  background: none;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
}

.admin-close:hover {
  color: #fff;
}

.admin-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.admin-section {
  margin-bottom: 24px;
}

.admin-section h3 {
  margin: 0 0 12px;
  font-size: 1rem;
  color: #fff;
}

.admin-empty {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.admin-user-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #2a2a3e;
  border-radius: 8px;
  margin-bottom: 8px;
}

.admin-user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.admin-user-name {
  font-weight: 500;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-user-email {
  font-size: 0.8rem;
  color: #888;
}

.admin-user-meta {
  font-size: 0.75rem;
  color: #666;
}

.admin-status-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.admin-status-approved {
  background: rgba(0, 212, 170, 0.15);
  color: #00d4aa;
}

.admin-status-suspended {
  background: rgba(255, 68, 68, 0.15);
  color: #ff4444;
}

.admin-status-pending {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
}

.admin-status-admin {
  background: rgba(100, 140, 255, 0.15);
  color: #648cff;
}

.admin-user-actions {
  display: flex;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

.btn-approve {
  padding: 6px 12px;
  background: #00d4aa;
  color: #1a1a2e;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-approve:hover {
  background: #00e4ba;
}

.btn-deny,
.btn-suspend {
  padding: 6px 12px;
  background: transparent;
  color: #ff6666;
  border: 1px solid #ff4444;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-deny:hover,
.btn-suspend:hover {
  background: rgba(255, 68, 68, 0.15);
}

.admin-base-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #2a2a3e;
  border-radius: 8px;
  margin-bottom: 8px;
}

.admin-base-name {
  font-weight: 500;
  color: #fff;
}

.admin-base-version {
  font-size: 0.8rem;
  color: #888;
}

.admin-user-limits {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.admin-user-limits label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #888;
}

.admin-max-profiles {
  width: 56px;
  padding: 4px 6px;
  background: #1e1e2e;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.8rem;
}

.admin-save-limits {
  padding: 4px 10px;
  background: transparent;
  color: #00d4aa;
  border: 1px solid #00d4aa;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.admin-save-limits:hover {
  background: rgba(0, 212, 170, 0.15);
}
`;

let adminStylesInjected = false;
export function injectAdminStyles(): void {
  if (adminStylesInjected) return;
  const style = document.createElement('style');
  style.textContent = adminStyles;
  document.head.appendChild(style);
  adminStylesInjected = true;
}
